import {useEffect, useRef, useState} from "react";
import play, {MatchSound} from "./sounds";
import {MatchTiming} from "./javaTypes";
import {ParsedMatchTiming} from "./utils";
import { Temporal } from "temporal-polyfill";

interface TimerParams {
    startAt?: number | null,
    duration: number | Temporal.Duration,
    nowCallback: () => number,
    sounds?: Record<number, MatchSound>,
    silenceUntil? : number
}

export interface TimerData {
    remaining: number,
    running: boolean,
    temporarilySilenced? : boolean
}

export interface MatchTimerData extends TimerData {
    preMatch: boolean,
    auto: boolean,
    transition: boolean,
    teleop: boolean,
    endgame: boolean,
    done: boolean
}

export function useTimer({
    startAt,
    duration,
    nowCallback,
    sounds,
    silenceUntil
} : TimerParams) : TimerData {
    const realDuration = duration instanceof Temporal.Duration ? duration.total('seconds') : duration;
    const [remaining, setRemaining] = useState(realDuration);
    const durationRef = useRef(realDuration);
    const playedTimestamps = useRef<Record<number, boolean>>({});
    const silenceUntilRef = useRef(silenceUntil);

    useEffect(() => {
        setRemaining(realDuration)
        durationRef.current = realDuration;
        playedTimestamps.current = {};
    }, [startAt, realDuration])
    useEffect(() => {
        silenceUntilRef.current = silenceUntil;
    }, [silenceUntil])
    useInterval(() => {
        let roundedTime = Math.ceil(durationRef.current - (nowCallback() - startAt!) / 1000)
        // By using rounded time we have a ~1 second buffer; go back 5 seconds to make sure we catch anything lingering
        for(let i = roundedTime + 5; i >= roundedTime; i--) {
            if (sounds && sounds[i] && !playedTimestamps.current[i]) {
                if (nowCallback() > (silenceUntilRef?.current ?? 0)) {
                    play(sounds[i])
                }
                playedTimestamps.current[i] = true
            }
        }
        setRemaining(Math.max(0, roundedTime))
    }, startAt && remaining > 0 ? 100 : null)
    // We recompute this here to ensure that a setstate is not required to immediately start returning the new value
    let currentRemaining = startAt ? Math.max(0, Math.ceil(realDuration - (nowCallback() - startAt) / 1000)) : 0

    return { remaining: currentRemaining, running: !!startAt && currentRemaining > 0, temporarilySilenced: nowCallback() < (silenceUntilRef.current ?? 0)};
}

// From https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback: () => void, delay : number | null) {
    const savedCallback = useRef<() => void>();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            if(savedCallback.current) {
                savedCallback.current();
            }
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

export function useMatchTimer(paramsObj : Omit<TimerParams, "duration">, timing: ParsedMatchTiming) : MatchTimerData {
    const totalSeconds = timing.total.total('seconds')
    const teleopSeconds = timing.teleop.total('seconds')
    const transitionSeconds = timing.transition.total('seconds')
    const endgameSeconds = timing.endgameWarning.total('seconds')
    const timer = useTimer({...paramsObj, duration: totalSeconds});
    return Object.assign({
        preMatch: timer.remaining > totalSeconds,
        auto: timer.running && timer.remaining > (teleopSeconds + transitionSeconds) && timer.remaining <= totalSeconds,
        transition: timer.running && timer.remaining > teleopSeconds && timer.remaining <= (teleopSeconds + transitionSeconds),
        teleop: timer.running && timer.remaining > endgameSeconds && timer.remaining <= teleopSeconds,
        endgame: timer.running && timer.remaining > 0 && timer.remaining <= endgameSeconds,
        done: !timer.running && timer.remaining <= 0
    }, timer);
}
